/**
 * Application Autofill review/confirm modal (TEC-489). Vendor-agnostic UI, driven entirely by
 * whatever `AutofillApplicationState` the server returns for this job's ATS vendor.
 *
 * Same modal shell/interaction pattern as TailorCvModal.tsx (overlay, Escape-to-close, body-scroll
 * lock, reset-on-close) so the two secondary apply-flow actions on this page feel consistent.
 * Per the architecture doc's resolved UX design: a native reconstruction of the target form's
 * fields (not a screenshot or the live page, there is no live page yet, the fill is pregenerated
 * before any browser session opens), every field inline-editable, a "Regenerate" control on the
 * free-text answer, one final "Submit to {company}" action, and explicit "nothing sent yet"
 * messaging until that action is taken.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, ArrowSquareOut, ArrowClockwise, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { Msg, useFormatMessage } from '../../../../../i18n-review/client/Msg';
import type { AutofillAnswerDraft, AutofillApplicationState } from '../../../../../types/user-state';
import type { DisplayJob } from '../../../../../types';

export interface AutofillApplyModalProps {
  job: DisplayJob | null;
  applyUrl: string;
  isOpen: boolean;
  onClose: () => void;
}

type ModalStatus = 'pregenerating' | 'reviewing' | 'submitting' | 'polling' | 'done' | 'error';

interface ErrorState {
  code: string;
  message: string;
}

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;

function jobContextForRequest(job: DisplayJob) {
  return {
    title: job.title,
    companyName: job.companyName,
    description: job.description,
    requirements: job.requirements,
    responsibilities: job.responsibilities,
  };
}

export const AutofillApplyModal: React.FC<AutofillApplyModalProps> = ({ job, applyUrl, isOpen, onClose }) => {
  const formatMessage = useFormatMessage();
  const jobId = job?.objectID ?? '';

  const [status, setStatus] = useState<ModalStatus>('pregenerating');
  const [error, setError] = useState<ErrorState | null>(null);
  const [autofill, setAutofill] = useState<AutofillApplicationState | null>(null);
  const [regeneratingQuestion, setRegeneratingQuestion] = useState<string | null>(null);
  const requestedRef = useRef(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Lock body scroll and close on Escape while open, same as TailorCvModal.
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Reset so reopening (e.g. for a different job) fetches/starts fresh.
  useEffect(() => {
    if (isOpen) return;
    requestedRef.current = false;
    setStatus('pregenerating');
    setError(null);
    setAutofill(null);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || requestedRef.current || !job) return;
    requestedRef.current = true;
    setStatus('pregenerating');
    setError(null);

    (async () => {
      try {
        const res = await fetch('/api/apply-autofill/pregenerate', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jobId,
            companyId: job.companyId ?? '',
            job: jobContextForRequest(job),
          }),
        });
        const body = await res.json();
        if (!res.ok) {
          setError({ code: body.error ?? 'failed', message: body.message ?? 'Something went wrong.' });
          setStatus('error');
          return;
        }
        setAutofill(body.autofill as AutofillApplicationState);
        setStatus('reviewing');
      } catch {
        setError({ code: 'network', message: 'Could not reach the server. Try again.' });
        setStatus('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, job]);

  const updateField = (field: keyof AutofillApplicationState['fields'], value: string) => {
    setAutofill((prev) => (prev ? { ...prev, fields: { ...prev.fields, [field]: value } } : prev));
  };

  const updateAnswer = (questionText: string, value: string) => {
    setAutofill((prev) =>
      prev ? { ...prev, answers: prev.answers.map((a) => (a.questionText === questionText ? { ...a, value } : a)) } : prev
    );
  };

  const regenerateAnswer = async (answer: AutofillAnswerDraft) => {
    if (!job) return;
    setRegeneratingQuestion(answer.questionText);
    try {
      const res = await fetch('/api/apply-autofill/regenerate-answer', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId, questionText: answer.questionText, job: jobContextForRequest(job) }),
      });
      const body = await res.json();
      if (res.ok) updateAnswer(answer.questionText, body.value);
    } finally {
      setRegeneratingQuestion(null);
    }
  };

  const pollForOutcome = () => {
    const startedAt = Date.now();
    const key = `autofillApplication#${jobId}`;
    const tick = async () => {
      try {
        const res = await fetch(`/api/user-state/${encodeURIComponent(key)}`, { credentials: 'include' });
        if (res.ok) {
          const body = await res.json();
          const state = body.data as AutofillApplicationState;
          if (state?.status === 'submitted' || state?.status === 'failed' || state?.status === 'manual_fallback') {
            setAutofill(state);
            setStatus('done');
            return;
          }
        }
      } catch {
        // Transient network hiccup, keep polling until the timeout.
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        setStatus('done'); // Surface as "still processing" via autofill.status remaining 'confirmed'.
        return;
      }
      pollTimeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    void tick();
  };

  const submitConfirmation = async () => {
    if (!autofill) return;
    setStatus('submitting');
    try {
      const res = await fetch('/api/apply-autofill/confirm', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId, fields: autofill.fields, answers: autofill.answers }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError({ code: body.error ?? 'failed', message: body.message ?? 'Something went wrong.' });
        setStatus('error');
        return;
      }
      setStatus('polling');
      pollForOutcome();
    } catch {
      setError({ code: 'network', message: 'Could not reach the server. Try again.' });
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label={formatMessage({ id: 'applyAutofill.pageTitle', defaultMessage: 'Review your application', description: 'Page title for the autofill review screen' })}
    >
      <div
        className="relative flex flex-col max-h-[90vh] w-full max-w-2xl bg-white dark:bg-[#1F1509] rounded-2xl shadow-2xl overflow-hidden border border-[#DDD5C8]/60 dark:border-[#4A3520]/50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#DDD5C8] dark:border-[#4A3520] shrink-0 bg-[#F2EADF]/40 dark:bg-[#2A1D0F]/50">
          <div className="min-w-0">
            <span className="text-base font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
              <Msg id="applyAutofill.pageTitle" defaultMessage="Review your application" description="Page title for the autofill review screen" />
            </span>
            {job ? (
              <p className="text-xs text-[#7A6E5F] dark:text-[#8A7060] truncate">
                {job.title} · {job.companyName}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[#EDE3D4] dark:hover:bg-[#342415] text-[#7A6E5F] dark:text-[#B8A48C] cursor-pointer shrink-0"
            aria-label={formatMessage({ id: 'profile.closeSuggestions', defaultMessage: 'Close', description: 'Close AI suggestions' })}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {status === 'pregenerating' && (
            <p className="text-sm text-[#5A4A3A] dark:text-[#B8A48C] text-center py-8">
              <Msg id="applyAutofill.preparing" defaultMessage="Preparing your application…" description="Loading state while pregenerating the autofill fill" />
            </p>
          )}

          {status === 'error' && error && (
            <div className="rounded-xl border border-[#E0C0A8] bg-[#FBF0E8] dark:bg-[#3A2418] dark:border-[#6A4A30] p-4 space-y-3">
              <p className="text-sm text-[#1E1A14] dark:text-[#F2EADF]">{error.message}</p>
              <a href={applyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-[#3B5E45] dark:text-[#98CFA8]">
                <Msg id="applyAutofill.applyManually" defaultMessage="Apply on the employer's site instead" description="Fallback link shown on any autofill error" />
                <ArrowSquareOut className="h-4 w-4" aria-hidden />
              </a>
            </div>
          )}

          {(status === 'reviewing' || status === 'submitting') && autofill && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  value={autofill.fields.firstName}
                  onChange={(e) => updateField('firstName', e.target.value)}
                  placeholder={formatMessage({ id: 'applyAutofill.firstName', defaultMessage: 'First name', description: 'Review-screen first-name field' })}
                  className="w-full rounded-xl border border-[#DDD5C8] bg-[#F8F5F0] px-3 py-2.5 text-sm dark:border-[#4A3520] dark:bg-[#2A1D0F]/50 dark:text-[#F2EADF]"
                />
                <input
                  type="text"
                  value={autofill.fields.lastName}
                  onChange={(e) => updateField('lastName', e.target.value)}
                  placeholder={formatMessage({ id: 'applyAutofill.lastName', defaultMessage: 'Last name', description: 'Review-screen last-name field' })}
                  className="w-full rounded-xl border border-[#DDD5C8] bg-[#F8F5F0] px-3 py-2.5 text-sm dark:border-[#4A3520] dark:bg-[#2A1D0F]/50 dark:text-[#F2EADF]"
                />
                <input
                  type="email"
                  value={autofill.fields.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder={formatMessage({ id: 'applyAutofill.email', defaultMessage: 'Email', description: 'Review-screen email field' })}
                  className="w-full rounded-xl border border-[#DDD5C8] bg-[#F8F5F0] px-3 py-2.5 text-sm dark:border-[#4A3520] dark:bg-[#2A1D0F]/50 dark:text-[#F2EADF]"
                />
                <input
                  type="tel"
                  value={autofill.fields.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder={formatMessage({ id: 'applyAutofill.phone', defaultMessage: 'Phone', description: 'Review-screen phone field' })}
                  className="w-full rounded-xl border border-[#DDD5C8] bg-[#F8F5F0] px-3 py-2.5 text-sm dark:border-[#4A3520] dark:bg-[#2A1D0F]/50 dark:text-[#F2EADF]"
                />
              </div>

              <div>
                <p className="text-xs font-semibold text-[#7A6E5F] dark:text-[#8A7060] mb-1">
                  <Msg id="applyAutofill.coverLetterLabel" defaultMessage="Cover letter (optional)" description="Label for the generic cover-letter textarea" />
                </p>
                <textarea
                  value={autofill.fields.coverLetter ?? ''}
                  onChange={(e) => updateField('coverLetter', e.target.value)}
                  rows={4}
                  className="w-full rounded-xl border border-[#DDD5C8] bg-[#F8F5F0] px-3 py-2.5 text-sm dark:border-[#4A3520] dark:bg-[#2A1D0F]/50 dark:text-[#F2EADF]"
                />
              </div>

              <div className="rounded-xl border border-[#DDD5C8] dark:border-[#4A3520] px-3 py-2.5 text-sm text-[#1E1A14] dark:text-[#F2EADF]">
                <Msg id="applyAutofill.resumeAttached" defaultMessage="resume.pdf will be attached" description="Résumé attachment chip on the review screen" />
              </div>

              {autofill.answers.map((answer) => (
                <div key={answer.questionText} className="space-y-1.5">
                  <p className="text-sm font-medium text-[#1E1A14] dark:text-[#F2EADF]">{answer.questionText}</p>
                  {answer.type === 'choice' && answer.choices ? (
                    <select
                      value={answer.value}
                      onChange={(e) => updateAnswer(answer.questionText, e.target.value)}
                      className="w-full rounded-xl border border-[#DDD5C8] bg-[#F8F5F0] px-3 py-2.5 text-sm dark:border-[#4A3520] dark:bg-[#2A1D0F]/50 dark:text-[#F2EADF]"
                    >
                      {answer.choices.map((choice) => (
                        <option key={choice} value={choice}>
                          {choice}
                        </option>
                      ))}
                    </select>
                  ) : answer.type === 'boolean' ? (
                    <select
                      value={answer.value}
                      onChange={(e) => updateAnswer(answer.questionText, e.target.value)}
                      className="w-full rounded-xl border border-[#DDD5C8] bg-[#F8F5F0] px-3 py-2.5 text-sm dark:border-[#4A3520] dark:bg-[#2A1D0F]/50 dark:text-[#F2EADF]"
                    >
                      <option value="true">{formatMessage({ id: 'applyAutofill.yes', defaultMessage: 'Yes', description: 'Boolean-question yes option' })}</option>
                      <option value="false">{formatMessage({ id: 'applyAutofill.no', defaultMessage: 'No', description: 'Boolean-question no option' })}</option>
                    </select>
                  ) : (
                    <div className="flex items-start gap-2">
                      <textarea
                        value={answer.value}
                        onChange={(e) => updateAnswer(answer.questionText, e.target.value)}
                        rows={3}
                        className="flex-1 rounded-xl border border-[#DDD5C8] bg-[#F8F5F0] px-3 py-2.5 text-sm dark:border-[#4A3520] dark:bg-[#2A1D0F]/50 dark:text-[#F2EADF]"
                      />
                      <button
                        type="button"
                        onClick={() => regenerateAnswer(answer)}
                        disabled={regeneratingQuestion === answer.questionText}
                        className="shrink-0 mt-1 p-2 rounded-lg border border-[#DDD5C8] dark:border-[#4A3520] text-[#5A4A3A] dark:text-[#B8A48C] hover:bg-[#F8F5F0] dark:hover:bg-[#2A1D0F]/50 cursor-pointer"
                        aria-label={formatMessage({ id: 'applyAutofill.regenerate', defaultMessage: 'Regenerate', description: 'Button to redraft one free-text answer' })}
                      >
                        <ArrowClockwise className={regeneratingQuestion === answer.questionText ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} aria-hidden />
                      </button>
                    </div>
                  )}
                </div>
              ))}

              <p className="text-xs text-[#7A6E5F] dark:text-[#8A7060] italic">
                <Msg id="applyAutofill.nothingSentYet" defaultMessage="Nothing has been sent to the employer yet." description="Trust-building notice shown above the final submit button" />
              </p>
            </div>
          )}

          {status === 'polling' && (
            <p className="text-sm text-[#5A4A3A] dark:text-[#B8A48C] text-center py-8">
              <Msg id="applyAutofill.polling" defaultMessage="Submitting your application now, this can take a minute…" description="Shown while waiting for the fill+submit Lambda to finish" />
            </p>
          )}

          {status === 'done' && autofill && (
            <div className="space-y-3">
              {autofill.status === 'submitted' ? (
                <div className="flex items-start gap-2 rounded-xl border border-[#C8DFC8] bg-[#F0F8F0] dark:bg-[#1A2E1E] dark:border-[#3A6A3A] p-4">
                  <CheckCircle className="h-5 w-5 shrink-0 text-[#3B5E45] dark:text-[#98CFA8]" aria-hidden />
                  <div>
                    <p className="text-sm font-medium text-[#1E1A14] dark:text-[#F2EADF]">
                      <Msg id="applyAutofill.success" defaultMessage="Application submitted." description="Success state after the fill+submit Lambda confirms a real submission" />
                    </p>
                    <a href="/tools" className="text-sm text-[#3B5E45] dark:text-[#98CFA8] font-semibold">
                      <Msg id="applyAutofill.viewInTracker" defaultMessage="View in your tracker" description="Link to the kanban board after a successful autofill submission" />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-[#E0C0A8] bg-[#FBF0E8] dark:bg-[#3A2418] dark:border-[#6A4A30] p-4 space-y-3">
                  <p className="text-sm text-[#1E1A14] dark:text-[#F2EADF]">
                    {autofill.failureMessage ?? (
                      <Msg id="applyAutofill.failedGeneric" defaultMessage="We couldn't finish submitting this application automatically." description="Generic failure message when a specific failureMessage isn't available" />
                    )}
                  </p>
                  <a href={applyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-[#3B5E45] dark:text-[#98CFA8]">
                    <Msg id="applyAutofill.applyManually" defaultMessage="Apply on the employer's site instead" description="Fallback link shown on any autofill error" />
                    <ArrowSquareOut className="h-4 w-4" aria-hidden />
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {(status === 'reviewing' || status === 'submitting') && autofill && (
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#DDD5C8] dark:border-[#4A3520] shrink-0">
            <button
              type="button"
              onClick={submitConfirmation}
              disabled={status === 'submitting'}
              className="rounded-xl bg-[#3B5E45] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60 cursor-pointer"
            >
              {status === 'submitting' ? (
                <Msg id="applyAutofill.submitting" defaultMessage="Submitting…" description="Submit button label while confirming" />
              ) : (
                formatMessage(
                  { id: 'applyAutofill.submitTo', defaultMessage: 'Submit to {company}', description: 'Final submit button, names the employer' },
                  { company: job?.companyName ?? '' }
                )
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
